import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import AllBookPage from '../AllData/AllBookPage';
import BookImage from './BookImage';
import './Style/OnlineReadBook.css';

const BOOK_IMAGE_CACHE = "book-images-v1";

const OnlineReadBook = () => {
  const { type, subject, topic } = useParams();

  const [downloaded, setDownloaded] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState("");

  // 1. Memoize matching pages based on URL params
  const matchingPages = useMemo(() => {
    return AllBookPage.filter(
      (book) =>
        book.type?.toLowerCase() === type?.toLowerCase() &&
        book.subject?.toLowerCase() === subject?.toLowerCase() &&
        book.topic?.toLowerCase() === topic?.toLowerCase()
    );
  }, [type, subject, topic]);

  // 2. Extract dynamic image list for the matching book pages
  const dynamicImageList = useMemo(() => {
    return matchingPages
      .map((page) => {
        if (!page.pagename || page.pagename.trim() === "") return null;
        return `/bookimg/${page.pagename.trim()}.jpg`;
      })
      .filter(Boolean);
  }, [matchingPages]);

  // 3. Download images into Cache Storage automatically
  const downloadImages = useCallback(async (imagesToDownload) => {
    if (imagesToDownload.length === 0) {
      setComplete(true);
      return;
    }

    setDownloading(true);
    setComplete(false);
    setError("");
    setDownloaded(0);

    try {
      const cache = await caches.open(BOOK_IMAGE_CACHE);
      let count = 0;

      for (const imageUrl of imagesToDownload) {
        const alreadyCached = await cache.match(imageUrl);

        if (!alreadyCached) {
          const response = await fetch(imageUrl);

          if (!response.ok) {
            throw new Error(`Unable to download: ${imageUrl}`);
          }
          await cache.put(imageUrl, response.clone());
        }
        count++;
        setDownloaded(count);
      }

      setComplete(true);
    } catch (err) {
      console.error(err);
      setError("Unable to download book pages. Please check your connection and try again.");
    } finally {
      setDownloading(false);
    }
  }, []);

  // 4. Automatically trigger download when matching pages/images are found
  useEffect(() => {
    if (matchingPages.length > 0) {
      if (dynamicImageList.length > 0) {
        downloadImages(dynamicImageList);
      } else {
        setComplete(true);
      }
    } else {
      setComplete(false);
    }
  }, [matchingPages.length, dynamicImageList, downloadImages]);

  const percentage = dynamicImageList.length === 0 ? 100 : Math.round((downloaded / dynamicImageList.length) * 100);

  return (
    <div className='onlineReadBookContainer'>
      <div className='onlineReadBookPagesSection'>
        
        {matchingPages.length > 0 ? (
          <>
            {/* Loading Progress State */}
            {downloading && (
              <div className="qil-progress-box" style={{ textAlign: "center", padding: "20px" }}>
                <p className="qil-subtitle">Please wait, downloading book pages...</p>
                <p>Downloading {downloaded} / {dynamicImageList.length} ({percentage}%)</p>
                <div className="qil-progress-bar-container" style={{ width: "100%", background: "#eee", height: "8px", borderRadius: "4px", margin: "10px 0" }}>
                  <div className="qil-progress-bar-fill" style={{ width: `${percentage}%`, background: "#4f46e5", height: "100%", borderRadius: "4px" }}></div>
                </div>
              </div>
            )}

            {/* Error & Retry Option */}
            {error && (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <p style={{ color: "red" }}>{error}</p>
                <button
                  className="retry-btn"
                  onClick={() => downloadImages(dynamicImageList)}
                  style={{ padding: "8px 16px", cursor: "pointer", marginTop: "10px" }}
                >
                  Retry Download
                </button>
              </div>
            )}

            {/* Show Book Images ONLY After Download is Complete */}
            {complete && (
              <div className="image-row-container">
                {matchingPages.map((page) => (
                  <BookImage
                    key={page.id}
                    src={`/bookimg/${page.pagename}.jpg`}
                    alt={page.pagename}
                    className="lazy-image"
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className='onlineReadBookNoPages'>
            <p>No matching pages found for this selection.</p>
          </div>
        )}

      </div>
    </div>
  );
};

export default OnlineReadBook;